import type { ComponentPropsWithoutRef, ComponentType, ElementRef, ForwardRefExoticComponent, RefAttributes } from 'react';
import { createElement, forwardRef } from 'react';
import { useEnsureConfigProvider } from './provider-guard';

type StaticMembers<C> = Omit<C, keyof React.ComponentType>;

type GuardedComponent<C extends ComponentType> = ForwardRefExoticComponent<ComponentPropsWithoutRef<C> & RefAttributes<ElementRef<C>>> &
  StaticMembers<C>;

export function createGuardedComponent<C extends ComponentType>(Component: C): GuardedComponent<C> {
  const name = Component.displayName ?? Component.name ?? 'Unknown';

  const Guarded = forwardRef<ElementRef<C>, ComponentPropsWithoutRef<C>>(function GuardedComponent(props, ref) {
    useEnsureConfigProvider(name);

    return createElement(Component, { ...props, ref });
  });

  Guarded.displayName = name;

  for (const key of Object.getOwnPropertyNames(Component)) {
    if (
      ['$$typeof', 'render', 'defaultProps', 'displayName', 'propTypes', 'name', 'length', 'caller', 'arguments', 'prototype'].includes(key)
    ) {
      continue;
    }

    const descriptor = Object.getOwnPropertyDescriptor(Component, key);

    if (descriptor) {
      Object.defineProperty(Guarded, key, descriptor);
    }
  }

  return Guarded as GuardedComponent<C>;
}
